// WhistleBlowPageDashboardChart - MODULE
// ***********************************************************************************************************************
/// <reference types="react-scripts" />
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { WhistleBlowStateFilter } from "ui/Scripts/source/models/enums";
import { AppUtils } from "../../../framework/appUtils";
import { IChartItem } from "../../../models/chart/chartItem";
import { ISavedChartPanel } from "../../../models/chart/savedChartPanel";
import { IReportParameterValue } from "../../../models/report/reportParameterValue";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import { IDashboardChartData } from "source/models/report/dashboardChartData";
import { DashboardChart } from "source/components/framework/DashboardChart";

export interface IWhistleBlowPageFilterState {
    selectedStartDate: number;
    selectedEndDate: number
    selectedUnitIds: string[];
    selectedCategoryIds: string[];
    selectedHandlerUserCodes: string[];
    selectedStateFilterId: string;
    selectedSeverityFilterId: string;
    selectedResolutionFilterId: string;
    selectedSourceFilterId: string;
    selectedAnonymityFilterId: string;
    selectedDynamicTimeFilterId: string;
}

export interface IWhistleBlowGeneralFilter {
    startDate: number;
    endDate: number;
    unitIds: string[];
    categoryIds: string[];
    handlerUserCodes: string[];
    stateFilterId: number;
    severityFilterId: number;
    resolutionFilterId: number;
    sourceFilterId: number;
    anonymityFilterId: number;
    dynamicTimeFilterId: number;
}

export interface IWhistleBlowGeneralFilterChange extends IWhistleBlowGeneralFilter {
    notChanged: boolean;
}

// WhistleBlowPageDashboardChart
export interface IWhistleBlowPageDashboardChartProp {
    chartPanel: ISavedChartPanel;
    chartItems: IChartItem[];
    onShowChartPanelPropertiesDialog: (panelId: string) => void;
}

export interface IWhistleBlowPageDashboardChartState {
    isLoading: boolean;
    chartData: IDashboardChartData;
    chartId: string;
    startDate: number;
    endDate: number;
    unitIds: string[];
    categoryIds: string[];
    handlerUserCodes: string[];
    state: number;
    severity: number;
    resolution: number;
    source: number;
    anonymity: number;
    dynamicTime: number;
    parameterValues: IReportParameterValue[]
}

export class WhistleBlowPageDashboardChart extends React.Component<IWhistleBlowPageDashboardChartProp, IWhistleBlowPageDashboardChartState> {
    static getGeneralFilterFromState = (state: IWhistleBlowPageFilterState): IWhistleBlowGeneralFilter => {
        return {
            startDate: state.selectedStartDate ? state.selectedStartDate : null,
            endDate: state.selectedEndDate ? state.selectedEndDate : null,
            unitIds: state.selectedUnitIds ? state.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [],
            categoryIds: state.selectedCategoryIds ? state.selectedCategoryIds.filter(i => i !== Base.emptyGuid) : [],
            handlerUserCodes: state.selectedHandlerUserCodes ? state.selectedHandlerUserCodes.filter(i => !!i) : [],
            stateFilterId: state.selectedStateFilterId ? parseInt(state.selectedStateFilterId, 10) : -2,
            severityFilterId: state.selectedSeverityFilterId ? parseInt(state.selectedSeverityFilterId, 10) : null,
            resolutionFilterId: state.selectedResolutionFilterId ? parseInt(state.selectedResolutionFilterId, 10) : null,
            sourceFilterId: state.selectedSourceFilterId ? parseInt(state.selectedSourceFilterId, 10) : null,
            anonymityFilterId: state.selectedAnonymityFilterId ? parseInt(state.selectedAnonymityFilterId, 10) : null,
            dynamicTimeFilterId: state.selectedDynamicTimeFilterId ? parseInt(state.selectedDynamicTimeFilterId, 10) : 0
        };
    };

    static getStateFilterPropsFromGeneralFilter = (generalFilter: any, defaultStateFilterId: string = "-2"): IWhistleBlowPageFilterState => {
        return {
            selectedStartDate: generalFilter && generalFilter.startDate ? generalFilter.startDate : 0,
            selectedEndDate: generalFilter && generalFilter.endDate ? generalFilter.endDate : 0,
            selectedUnitIds: generalFilter && generalFilter.unitIds && generalFilter.unitIds.length > 0 ? generalFilter.unitIds : [Base.emptyGuid],
            selectedCategoryIds: generalFilter && generalFilter.categoryIds && generalFilter.categoryIds.length > 0 ? generalFilter.categoryIds : [Base.emptyGuid],
            selectedHandlerUserCodes: generalFilter && generalFilter.handlerUserCodes && generalFilter.handlerUserCodes.length > 0 ? generalFilter.handlerUserCodes : [""],
            selectedStateFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.stateFilterId) ? generalFilter.stateFilterId.toString(10) : defaultStateFilterId,
            selectedSeverityFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.severityFilterId) ? generalFilter.severityFilterId.toString(10) : "",
            selectedResolutionFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.resolutionFilterId) ? generalFilter.resolutionFilterId.toString(10) : "",
            selectedSourceFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.sourceFilterId) ? generalFilter.sourceFilterId.toString(10) : "",
            selectedAnonymityFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.anonymityFilterId) ? generalFilter.anonymityFilterId.toString(10) : "",
            selectedDynamicTimeFilterId: generalFilter && !Base.isNullOrUndefined(generalFilter.dynamicTimeFilterId) ? generalFilter.dynamicTimeFilterId.toString(10) : "0",
        };
    };

    static getWhistleBlowGeneralFilterChange(prevProps: IWhistleBlowPageFilterState, props: IWhistleBlowPageFilterState): IWhistleBlowGeneralFilterChange {
        const prevValues = WhistleBlowPageDashboardChart.getGeneralFilterFromState(prevProps);
        const values = WhistleBlowPageDashboardChart.getGeneralFilterFromState(props);
        return {
            ...values,
            notChanged: (prevValues.startDate ?? -99).toString(10) === (values.startDate ?? -99).toString(10) &&
                (prevValues.endDate ?? -99).toString(10) === (values.endDate ?? -99).toString(10) &&
                prevValues.stateFilterId.toString(10) === values.stateFilterId.toString(10) &&
                (prevValues.severityFilterId ?? -99).toString(10) === (values.severityFilterId ?? -99).toString(10) &&
                (prevValues.resolutionFilterId ?? -99).toString(10) === (values.resolutionFilterId ?? -99).toString(10) &&
                (prevValues.sourceFilterId ?? -99).toString(10) === (values.sourceFilterId ?? -99).toString(10) &&
                (prevValues.anonymityFilterId ?? -99).toString(10) === (values.anonymityFilterId ?? -99).toString(10) &&
                prevValues.dynamicTimeFilterId.toString(10) === values.dynamicTimeFilterId.toString(10) &&
                Base.equalStrArrays(prevValues.categoryIds, values.categoryIds) &&
                Base.equalStrArrays(prevValues.handlerUserCodes, values.handlerUserCodes) &&
                Base.equalStrArrays(prevValues.unitIds, values.unitIds)
        };
    }

    getStateChangeFromProps = (props: IWhistleBlowPageDashboardChartProp): any => {
        const chartItem = props.chartPanel && props.chartItems
            ? props.chartItems.find(i => i.reportName === props.chartPanel.reportId)
            : null;
        const reportFilterProps = WhistleBlowPageDashboardChart.getStateFilterPropsFromGeneralFilter(props.chartPanel?.generalFilter, WhistleBlowStateFilter.All.toString(10));
        const result = {
            startDate: reportFilterProps.selectedStartDate ? reportFilterProps.selectedStartDate : null,
            endDate: reportFilterProps.selectedEndDate ? reportFilterProps.selectedEndDate : null,
            unitIds: reportFilterProps.selectedUnitIds ? reportFilterProps.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [],
            categoryIds: reportFilterProps.selectedCategoryIds ? reportFilterProps.selectedCategoryIds.filter(i => i !== Base.emptyGuid) : [],
            handlerUserCodes: reportFilterProps.selectedHandlerUserCodes ? reportFilterProps.selectedHandlerUserCodes.filter(i => !!i) : [],
            state: reportFilterProps.selectedStateFilterId ? reportFilterProps.selectedStateFilterId.toInteger(true, -2) : null,
            severity: reportFilterProps.selectedSeverityFilterId ? reportFilterProps.selectedSeverityFilterId.toInteger() : null,
            resolution: reportFilterProps.selectedResolutionFilterId ? reportFilterProps.selectedResolutionFilterId.toInteger() : null,
            source: reportFilterProps.selectedSourceFilterId ? reportFilterProps.selectedSourceFilterId.toInteger() : null,
            anonymity: reportFilterProps.selectedAnonymityFilterId ? reportFilterProps.selectedAnonymityFilterId.toInteger() : null,
            dynamicTime: reportFilterProps.selectedDynamicTimeFilterId ? reportFilterProps.selectedDynamicTimeFilterId.toInteger() : 0,
            chartId: props.chartPanel?.chartId ?? "",
            parameterValues: props.chartPanel && chartItem ? props.chartPanel.getReportParameterValues(chartItem) : []
        };
        return result;
    };

    constructor(props: IWhistleBlowPageDashboardChartProp) {
        super(props);
        this.state = {
            ...this.getStateChangeFromProps(props),
            chart: "",
            isLoading: true
        };
    }

    createChart = async () => {
        const props = this.props;
        const state = this.state;
        if (!props.chartPanel || !props.chartItems || state.parameterValues.length < 1) return;
        this.setState({ isLoading: true });
        const chartData = await AppUtils.callService(() => whistleBlowService.getChart(props.chartPanel.reportId, props.chartPanel.chartId,
            state.startDate, state.endDate, state.dynamicTime, state.unitIds, state.categoryIds, state.handlerUserCodes, state.state, state.severity, state.resolution,
            state.source, state.anonymity, state.parameterValues), false, true, () => { this.setState({ isLoading: false }); });
        this.setState({
            chartData
        });
    };

    componentDidUpdate(prevProps: IWhistleBlowPageDashboardChartProp) {
        const state = this.state;
        const newState = this.getStateChangeFromProps(this.props);
        const prevChartId = state.chartId;
        const chartId = newState.chartId;
        const prevStartDate = state.startDate ?? -99;
        const startDate = newState.startDate ?? -99;
        const prevEndDate = state.endDate ?? -99;
        const endDate = newState.endDate ?? -99;
        const prevUnitIds = state.unitIds ? state.unitIds : [];
        const unitIds = newState.unitIds ? newState.unitIds : [];
        const prevCategoryIds = state.categoryIds ? state.categoryIds : [];
        const categoryIds = newState.categoryIds ? newState.categoryIds : [];
        const prevHandlerUserCodes = state.handlerUserCodes ? state.handlerUserCodes : [];
        const handlerUserCodes = newState.handlerUserCodes ? newState.handlerUserCodes : [];
        const prevAState = state.state ?? -99;
        const aState = newState.state ?? -99;
        const prevSeverity = state.severity ?? -99;
        const severity = newState.severity ?? -99;
        const prevResolution = state.resolution ?? -99;
        const resolution = newState.resolution ?? -99;
        const prevSource = state.source ?? -99;
        const source = newState.source ?? -99;
        const prevAnonymity = state.anonymity ?? -99;
        const anonymity = newState.anonymity ?? -99;
        const prevDynamicTime = state.dynamicTime ?? 0;
        const dynamicTime = newState.dynamicTime ?? 0;
        const prevParameterValues = state.parameterValues ?? [];
        const parameterValues = newState.parameterValues ?? [];
        if (prevChartId === chartId &&
            prevStartDate.toString(10) === startDate.toString(10) &&
            prevEndDate.toString(10) === endDate.toString(10) &&
            prevAState.toString(10) === aState.toString(10) &&
            prevSeverity.toString(10) === severity.toString(10) &&
            prevResolution.toString(10) === resolution.toString(10) &&
            prevSource.toString(10) === source.toString(10) &&
            prevAnonymity.toString(10) === anonymity.toString(10) &&
            prevDynamicTime.toString(10) === dynamicTime.toString(10) &&
            Base.equalStrArrays(prevCategoryIds, categoryIds) &&
            Base.equalStrArrays(prevHandlerUserCodes, handlerUserCodes) &&
            Base.equalStrArrays(prevUnitIds, unitIds) &&
            JSON.stringify(prevParameterValues) === JSON.stringify(parameterValues)) return;
        this.setState({
            ...newState
        }, this.createChart);
    }

    componentDidMount(): void {
        this.createChart();
    }
    
    render() {
        const props = this.props;
        const state = this.state;
        const chartPanel = props.chartPanel;
        if (!chartPanel) return null;
        return (
            <DashboardChart
                panelId={chartPanel.id}
                title={chartPanel.getTitle()}
                chartData={state.chartData}
                isLoading={state.isLoading}
                onShowProperties={props.onShowChartPanelPropertiesDialog}
            />
        );
    }
}